import { z } from 'zod';
import type { Action, Subject } from './ability';
import type { RoleCode } from './constants';

/**
 * Granular permissions (PRD §3.1, SET-07). Each maps to one CASL rule. Roles are sets of
 * these codes stored in `role_permissions` and editable by admins; Admin itself always
 * has every permission. For area-scoped grants (technicians) rules on area-bound subjects
 * are limited to the grant's area automatically.
 */
export interface PermissionDef {
  code: string;
  action: Action;
  subject: Exclude<Subject, 'all'>;
  group: string;
  label: string;
}

export const PERMISSIONS = [
  {
    code: 'asset.read',
    action: 'read',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Lihat aset dan pelanggan',
  },
  {
    code: 'asset.create',
    action: 'create',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Tambah aset',
  },
  {
    code: 'asset.update',
    action: 'update',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Edit data teknis (kapasitas, splitter, nama port)',
  },
  {
    code: 'asset.field_update',
    action: 'field_update',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Update lapangan (status, koneksi port, geser ±50 m)',
  },
  {
    code: 'asset.delete',
    action: 'delete',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Hapus dan pulihkan aset',
  },
  {
    code: 'asset.import',
    action: 'import',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Import KMZ/KML',
  },
  {
    code: 'asset.export',
    action: 'export',
    subject: 'Asset',
    group: 'Aset & pelanggan',
    label: 'Export aset dan pelanggan',
  },
  { code: 'route.read', action: 'read', subject: 'Route', group: 'Jalur kabel', label: 'Lihat jalur kabel' },
  { code: 'route.create', action: 'create', subject: 'Route', group: 'Jalur kabel', label: 'Buat jalur' },
  {
    code: 'route.update',
    action: 'update',
    subject: 'Route',
    group: 'Jalur kabel',
    label: 'Edit bentuk jalur dan tiang otomatis',
  },
  {
    code: 'route.field_update',
    action: 'field_update',
    subject: 'Route',
    group: 'Jalur kabel',
    label: 'Update lapangan jalur (status, catatan)',
  },
  {
    code: 'route.delete',
    action: 'delete',
    subject: 'Route',
    group: 'Jalur kabel',
    label: 'Hapus dan pulihkan jalur',
  },
  { code: 'route.export', action: 'export', subject: 'Route', group: 'Jalur kabel', label: 'Export jalur' },
  { code: 'area.read', action: 'read', subject: 'Area', group: 'Wilayah', label: 'Lihat wilayah' },
  { code: 'area.update', action: 'update', subject: 'Area', group: 'Wilayah', label: 'Gambar batas wilayah' },
  {
    code: 'monitoring.read',
    action: 'read',
    subject: 'Monitoring',
    group: 'Monitoring & insiden',
    label: 'Lihat status perangkat dan grafik',
  },
  {
    code: 'monitoring.ping',
    action: 'ping',
    subject: 'Monitoring',
    group: 'Monitoring & insiden',
    label: 'Ping manual',
  },
  {
    code: 'monitoring.configure',
    action: 'configure',
    subject: 'Monitoring',
    group: 'Monitoring & insiden',
    label: 'Atur monitoring, SNMP dan maintenance',
  },
  {
    code: 'alert.read',
    action: 'read',
    subject: 'Alert',
    group: 'Monitoring & insiden',
    label: 'Lihat insiden',
  },
  {
    code: 'alert.ack',
    action: 'ack',
    subject: 'Alert',
    group: 'Monitoring & insiden',
    label: 'Akui dan tutup insiden',
  },
  { code: 'pricing.read', action: 'read', subject: 'Pricing', group: 'Biaya', label: 'Lihat biaya dan BOQ' },
  {
    code: 'pricing.manage',
    action: 'manage',
    subject: 'Pricing',
    group: 'Biaya',
    label: 'Ubah harga default',
  },
  {
    code: 'masterdata.manage',
    action: 'manage',
    subject: 'MasterData',
    group: 'Sistem',
    label: 'Kelola master data (jenis aset, splitter, warna, tipe kabel)',
  },
  {
    code: 'user.manage',
    action: 'manage',
    subject: 'User',
    group: 'Sistem',
    label: 'Kelola pengguna, pemetaan role dan hak akses',
  },
  { code: 'auditlog.read', action: 'read', subject: 'AuditLog', group: 'Sistem', label: 'Lihat audit log' },
  {
    code: 'settings.manage',
    action: 'manage',
    subject: 'Settings',
    group: 'Sistem',
    label: 'Pengaturan sistem (wilayah, monitoring default)',
  },
  {
    code: 'mcp.use',
    action: 'use',
    subject: 'Mcp',
    group: 'Integrasi',
    label: 'Akses AI Agent (MCP) dengan hak akses peran ini',
  },
] as const satisfies readonly PermissionDef[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];
export const PERMISSION_CODES = PERMISSIONS.map((p) => p.code) as PermissionCode[];

/** Subjects whose records belong to an area; area-scoped grants only reach their own area. */
export const AREA_BOUND_SUBJECTS: ReadonlyArray<Subject> = ['Asset', 'Route', 'Monitoring', 'Alert'];

/** Permission set per role (Admin is implicit: everything). */
export type RoleMatrix = Partial<Record<RoleCode, readonly string[]>>;

/** Defaults = PRD §3.1. Seeds `role_permissions`; "Kembalikan default" restores these. */
export const DEFAULT_ROLE_PERMISSIONS: Record<Exclude<RoleCode, 'admin'>, readonly PermissionCode[]> = {
  noc: [
    'asset.read',
    'asset.export',
    'route.read',
    'route.export',
    'area.read',
    'monitoring.read',
    'monitoring.ping',
    'monitoring.configure',
    'alert.read',
    'alert.ack',
    'mcp.use',
  ],
  planner: [
    'asset.read',
    'asset.create',
    'asset.update',
    'asset.field_update',
    'asset.delete',
    'asset.import',
    'asset.export',
    'route.read',
    'route.create',
    'route.update',
    'route.field_update',
    'route.delete',
    'route.export',
    'area.read',
    'area.update',
    'monitoring.read',
    'alert.read',
    'pricing.read',
    'pricing.manage',
    'masterdata.manage',
    'mcp.use',
  ],
  teknisi: [
    'asset.read',
    'asset.create',
    'asset.field_update',
    'asset.export',
    'route.read',
    'route.create',
    'route.field_update',
    'route.export',
    'area.read',
    'monitoring.read',
    'monitoring.ping',
    'alert.read',
  ],
};

export const EDITABLE_ROLES = ['noc', 'planner', 'teknisi'] as const;

export const rolePermissionsSchema = z.object({
  permissions: z.array(z.enum(PERMISSION_CODES as [string, ...string[]])).max(PERMISSIONS.length),
});

export function permissionDef(code: string): PermissionDef | undefined {
  return PERMISSIONS.find((p) => p.code === code);
}
